<template>
  <Layout
    ref="layout"
    :error-message="errorMessage"
    :is-loading="isLoading"
    class="nb-iot-container"
    :top-offset="topOffset"
  >
    <!-- Map base layer — always visible as full-viewport canvas -->
    <template slot="map-content">
      <Map
        v-if="!requiresRefresh && hasData"
        :items="mapItems"
        is-grey-scale
        :latest-point-is-uncertain="isInMotionStationaryOverlap"
        height="100vh"
      />
    </template>

    <!-- Timeline FAB (open / close timeline history) -->
    <v-btn
      v-if="!requiresRefresh && hasData && !isTimelineExpanded"
      dark
      small
      rounded
      class="timeline-fab timeline-fab--with-label"
      @click="isTimelineExpanded = true"
    >
      <v-icon left>
        mdi-history
      </v-icon>
      <span class="timeline-fab__label">Timeline</span>
    </v-btn>

    <!-- Timeline bottom sheet (resizable, height persisted) -->
    <transition name="timeline-sheet">
      <div
        v-if="isTimelineExpanded"
        ref="timelineSheet"
        class="timeline-sheet"
        :style="{ height: timelineSheetHeightVh + 'vh' }"
      >
        <div
          class="timeline-sheet__header"
          @mousedown="startTimelineSheetDrag"
          @touchstart="startTimelineSheetDrag"
        >
          <div class="timeline-sheet__header-left">
            <v-icon
              small
              color="#45BEC1"
              class="mr-3"
              @click="isTimelineExpanded = false"
            >
              mdi-close
            </v-icon>
            <span class="timeline-sheet__title">
              Location History
            </span>
          </div>
          <div
            class="timeline-sheet__header-drag"
            :class="{ 'timeline-sheet__header-drag--active': isDraggingTimelineSheet }"
          >
            <div class="timeline-sheet__grip">
              <div class="timeline-sheet__grip-line" />
              <div class="timeline-sheet__grip-line" />
            </div>
          </div>
          <div class="timeline-sheet__header-right" />
        </div>
        <div class="timeline-sheet__content">
          <Timeline
            :items="timelineItems"
            hide-right-if-same
          />
        </div>
      </div>
    </transition>

    <fragment>
      <RefreshMessage v-if="requiresRefresh" />
      <v-card
        v-if="!requiresRefresh && isLoading"
        class="tracking-info-card"
        elevation="4"
      >
        <v-card-text class="tracking-info-card__content tracking-info-card__skeleton">
          <div class="skeleton-row skeleton-row--horizontal">
            <div class="skeleton-col">
              <div class="skeleton-bar skeleton-bar--label" />
              <div class="skeleton-bar skeleton-bar--value" />
            </div>
            <div class="skeleton-col">
              <div class="skeleton-chip-row">
                <div class="skeleton-bar skeleton-bar--label" />
                <div class="skeleton-bar skeleton-bar--chip" />
              </div>
              <div class="skeleton-bar skeleton-bar--subscript" />
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="tracking-info-card__actions">
          <div class="skeleton-bar skeleton-bar--icon" />
          <div class="skeleton-bar skeleton-bar--toggle-text" />
          <v-spacer />
          <div class="skeleton-bar skeleton-bar--icon" />
        </v-card-actions>
      </v-card>
      <v-card
        v-else-if="hasData && !requiresRefresh"
        class="tracking-info-card"
        elevation="4"
      >
        <v-card-text class="tracking-info-card__content">
          <div class="tracking-info-card__row">
            <div class="tracking-info-card__reference">
              <div class="tracking-info-card__label">
                TRACKING REFERENCE
              </div>
              <div class="tracking-info-card__value">
                {{ nbIotData.name }}
              </div>
            </div>
            <div class="tracking-info-card__status">
              <div class="tracking-info-card__chip-row">
                <div class="tracking-info-card__label">
                  STATUS
                </div>
                <v-chip
                  x-small
                  :color="statusColor"
                  dark
                  class="tracking-info-card__chip"
                >
                  {{ status }}
                </v-chip>
              </div>
              <div class="tracking-info-card__subscript">
                {{ infoText }} {{ getTimeAgo(nbIotData.lastSeen) }}
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions
          class="tracking-info-card__actions"
          @click="isExpanded = !isExpanded"
        >
          <v-icon
            small
            color="#45BEC1"
            class="mr-1"
          >
            mdi-information-outline
          </v-icon>
          <span class="tracking-info-card__actions-text">
            {{ isExpanded ? "Hide Details" : "Full Details" }}
          </span>
          <v-spacer />
          <v-icon
            small
            class="tracking-info-card__chevron"
            :class="{ 'tracking-info-card__chevron--open': isExpanded }"
          >
            mdi-chevron-down
          </v-icon>
        </v-card-actions>
        <v-expand-transition>
          <div v-show="isExpanded">
            <v-divider />
            <div class="tracking-info-card__details-content">
              <div class="tracking-info-card__detail-item">
                <div class="tracking-info-card__label">
                  CURRENT LOCATION
                </div>
                <div
                  v-if="currentLocation"
                  class="tracking-info-card__detail-value
                    tracking-info-card__detail-value--highlight"
                >
                  {{ currentLocation }}
                </div>
                <div
                  v-else
                  class="skeleton-bar skeleton-bar--detail"
                />
              </div>
              <div
                v-if="latestTemperature"
                class="tracking-info-card__detail-item"
              >
                <div class="tracking-info-card__label">
                  TEMPERATURE
                </div>
                <template v-if="latestTemperatureDisplayValue">
                  <div class="tracking-info-card__temp-row">
                    <span class="tracking-info-card__temp-number">
                      {{ temperatureNumericValue }}
                    </span>
                    <span class="tracking-info-card__temp-unit">
                      {{ isCelsius ? '°C' : '°F' }}
                    </span>
                    <div
                      class="tracking-info-card__unit-toggle"
                      role="radiogroup"
                      aria-label="Temperature unit"
                    >
                      <button
                        class="tracking-info-card__unit-btn"
                        :class="{ 'tracking-info-card__unit-btn--active': isCelsius }"
                        :aria-pressed="String(isCelsius)"
                        @click.stop="setTemperatureUnit(true)"
                      >
                        °C
                      </button>
                      <button
                        class="tracking-info-card__unit-btn"
                        :class="{ 'tracking-info-card__unit-btn--active': !isCelsius }"
                        :aria-pressed="String(!isCelsius)"
                        @click.stop="setTemperatureUnit(false)"
                      >
                        °F
                      </button>
                    </div>
                  </div>
                  <div>
                    <a
                      class="tracking-info-card__temp-link"
                      role="button"
                      tabindex="0"
                      @click.stop="displayTemperatureChart"
                      @keydown.enter="displayTemperatureChart"
                    >
                      Show History
                    </a>
                  </div>
                </template>
                <div
                  v-else-if="temperatureTimedOut"
                  class="tracking-info-card__detail-value tracking-info-card__detail-value--muted"
                >
                  No data available
                </div>
                <div
                  v-else
                  class="skeleton-bar skeleton-bar--detail"
                />
              </div>
              <div class="tracking-info-card__detail-item">
                <div class="tracking-info-card__label">
                  ORIGIN
                </div>
                <div
                  v-if="origin"
                  class="tracking-info-card__detail-value"
                >
                  {{ origin }}
                </div>
                <div
                  v-else
                  class="skeleton-bar skeleton-bar--detail"
                />
              </div>
              <div class="tracking-info-card__detail-item">
                <div class="tracking-info-card__label">
                  ACTIVATED
                </div>
                <div
                  v-if="activated"
                  class="tracking-info-card__detail-value"
                >
                  {{ activated }}
                </div>
                <div
                  v-else
                  class="skeleton-bar skeleton-bar--detail"
                />
              </div>
            </div>
          </div>
        </v-expand-transition>
      </v-card>
      <TemperatureDialog
        v-if="hasData && hasAssetId"
        :asset-id="assetId"
        :is-visible="temperatureChartIsVisible"
        :asset-name="nbIotData.name"
        :temperatures="temperatures.items"
        :timeline-data="timelineItems"
        @close="closeTemperatureChart"
      />
    </fragment>
  </Layout>
</template>
<script>
import dateTimeHelper from '../../../mixins/date-time-helper';
import { constructLocationTimelineItemsForAsset } from '../../../helpers/asset-timeline-helper';
import Layout from '../layout/nb-iot-layout';
import Timeline from '../../molecules/timeline/timeline';
import Map from '../../molecules/map/map-with-loading';
import RefreshMessage from '../../atoms/message/refresh-message';
import TemperatureDialog from './temperature-dialog';

import {
  convertCelsiusToFahrenheit, locationStates,
  pollingNbIotDataInterval, temperatureUnits,
  routeNames,
} from '../../../constants';
import constructDisplayAddress from '../../../helpers/nb-iot-current-location-helper';
import { getBrowserTimeZone } from '../../../helpers/date-formatting-helper';

export default {
  name: 'NbIotTrackingScreen',
  components: {
    Layout,
    Timeline,
    Map,
    RefreshMessage,
    TemperatureDialog,
  },
  mixins: [dateTimeHelper],
  props: {
    assetId: { type: String, default: null },
    t: { type: String, default: '' },
    bleId: { type: String, default: null },
    topOffset: { type: Number, default: 0 },
  },
  data() {
    return {
      timelineHeight: 0,
      hasLoadedData: false,
      isLargeScreen: false,
      temperatureChartIsVisible: false,

      isExpanded: false,
      isTimelineExpanded: false,
      temperatureTimedOut: false,
      temperatureTimeoutId: null,
      timelineSheetHeightVh: 40,
      isDraggingTimelineSheet: false,
      timelineSheetDragStartY: 0,
      timelineSheetDragStartHeightVh: 0,
    };
  },
  computed: {
    errorMessage() { return this.$store.state.nbIot.errorMessage; },
    isLoading() { return this.$store.state.nbIot.isLoading; },
    hasAssetId() { return !!this.assetId; },
    nbIotData() { return this.$store.state.nbIot.data; },
    requiresActivationData() {
      return !!this.nbIotData && this.nbIotData.requiresActivationData;
    },
    hasData() {
      return !this.errorMessage && !this.isLoading
       && !!this.nbIotData && !this.requiresActivationData;
    },
    status() { return this.hasData ? (this.nbIotData.status || 'unknown') : 'Loading...'; },
    statusColor() {
      if (['Offline', 'Not Activated', 'Suspended'].includes(this.status)) {
        return '#6B7273';
      }
      if (this.status === 'Connecting' || this.status === 'Low Signal') {
        return '#EAB839';
      }
      return '#45BEC1';
    },
    infoText() {
      return !this.latestLocation.state
        || this.latestLocation.state === locationStates.stationary
        || this.latestLocation.state === locationStates.lowSignal
        ? 'Last seen'
        : 'In motion';
    },
    origin() {
      return this.hasData && this.hasLocations
        ? this.getAddressDisplayName(this.nbIotData.origin)
        : 'Loading...';
    },
    activated() {
      if (!this.hasData || !this.nbIotData.activatedAt) {
        return 'Activating...';
      }
      const date = new Date(this.nbIotData.activatedAt);
      const nowYear = new Date().getFullYear();
      const activatedDateString = date.toLocaleString(navigator.language, {
        timeZone: getBrowserTimeZone(),
        year: date.getFullYear() !== nowYear ? 'numeric' : undefined,
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      return `${activatedDateString} (${this.getTimeAgo(this.nbIotData.activatedAt)})`;
    },
    currentLocation() {
      return this.locations.items.length > 0
        ? this.getAddressDisplayName(this.locations.items[0].address)
        : 'Loading...';
    },
    locations() {
      return Array.isArray((this.$store.state.nbIot.locations || {}).items)
        ? this.$store.state.nbIot.locations
        : { items: [] };
    },
    hasLocations() { return this.locations.items.length > 0; },
    latestLocation() {
      return this.locations.items.length > 0 ? this.locations.items[0] : {};
    },
    temperatures() {
      return Array.isArray((this.$store.state.nbIot.temperatures || {}).items)
        ? this.$store.state.nbIot.temperatures
        : { items: [] };
    },
    latestTemperature() {
      return this.temperatures.items.length > 0 ? this.temperatures.items[0] : null;
    },
    latestTemperatureDisplayValue() {
      if (!this.latestTemperature) return 'Loading...';

      return this.temperatureNumericValue
        + (this.isCelsius ? temperatureUnits.celsius : temperatureUnits.fahrenheit);
    },
    temperatureNumericValue() {
      if (!this.latestTemperature) return null;

      return this.isCelsius
        ? `${this.latestTemperature.degreesCelcius}`
        : `${convertCelsiusToFahrenheit(this.latestTemperature.degreesCelcius)}`;
    },
    isCelsius() {
      return this.$store.getters['user/isCelsiusTemperatureUnits'];
    },
    isInMotionStationaryOverlap() {
      return this.latestLocation.state === locationStates.inMotionStationaryOverlap;
    },
    timelineItems() {
      return constructLocationTimelineItemsForAsset(
        {
          // need to take copy, as function sorts the locations
          locations: { items: [...((this.locations || {}).items || [])] },
          labelAddedOn: this.nbIotData.activatedAt,
          name: this.nbIotData.name,
        },
        true,
        true,
      );
    },
    mapItems() {
      return [...this.locations.items.map((item) => ({
        longitude: item.coordinate.longitude,
        latitude: item.coordinate.latitude,
        error: item.accuracy,
        properties: {
          id: this.assetId,
          name: (item.address || {}).city || '',
          timestamp: item.timestamp,
          lastUpdatedAt: item.lastUpdatedAt,
          address: item.address,
          coordinate: item.coordinate,
          accuracy: item.accuracy,
        },
      }))];
    },
    gotDataAt() { return this.$store.state.nbIot.lastGotDataAt; },
    requiresRefresh() { return this.$store.state.nbIot.exceededMaxPollingDuration; },
  },
  watch: {
    nbIotData() {
      if (this.requiresActivationData) {
        // asset requires activation data - route to activation screen
        this.$router.replace({
          name: routeNames.nbIot.link,
          query: { t: this.t, b: this.nbIotData.bleId },
        });
      } else if (!this.hasAssetId && this.hasData) {
        this.$router.replace({ query: { ...this.$route.query, assetId: this.nbIotData.id } });
      }
    },
  },
  created() {
    this.setupDataPolling();
    this.timelineSheetHeightVh = this.getTimelineSheetHeightFromStorage();
  },
  beforeDestroy() {
    this.tearDownDataPolling();
    this.endTimelineSheetDrag();
  },
  methods: {
    getTimelineSheetHeightFromStorage() {
      try {
        const v = localStorage.getItem('nbIotTimelineSheetHeightVh');
        if (v == null) return 40;
        const n = Number(v);
        return Number.isFinite(n) ? Math.min(85, Math.max(15, n)) : 40;
      } catch {
        return 40;
      }
    },
    persistTimelineSheetHeight() {
      try {
        localStorage.setItem('nbIotTimelineSheetHeightVh', String(this.timelineSheetHeightVh));
      } catch {
        // ignore
      }
    },
    getClientYFromEvent(e) {
      if (e.clientY != null) return e.clientY;
      if (e.touches && e.touches.length > 0) return e.touches[0].clientY;
      return 0;
    },
    startTimelineSheetDrag(e) {
      // don't start drag if clicking on interactive elements in the header (e.g. close button)
      if (e.target.closest('.v-icon')) return;
      e.preventDefault();
      this.timelineSheetDragStartY = this.getClientYFromEvent(e);
      this.timelineSheetDragStartHeightVh = this.timelineSheetHeightVh;
      this.isDraggingTimelineSheet = true;
      document.addEventListener('mousemove', this.onTimelineSheetDrag, { passive: false });
      document.addEventListener('mouseup', this.endTimelineSheetDrag);
      document.addEventListener('touchmove', this.onTimelineSheetDrag, { passive: false });
      document.addEventListener('touchend', this.endTimelineSheetDrag);
    },
    onTimelineSheetDrag(e) {
      const clientY = this.getClientYFromEvent(e);
      const heightFromBottomPx = window.innerHeight - clientY;
      const heightVh = (heightFromBottomPx / window.innerHeight) * 100;
      const CLOSE_THRESHOLD_VH = 8;
      const MIN_VH = 15;
      const MAX_VH = 85;
      const DEFAULT_VH = 40;

      if (heightVh < CLOSE_THRESHOLD_VH) {
        this.isTimelineExpanded = false;
        this.timelineSheetHeightVh = DEFAULT_VH;
        this.persistTimelineSheetHeight();
        this.endTimelineSheetDrag();
        return;
      }
      this.timelineSheetHeightVh = Math.min(MAX_VH, Math.max(MIN_VH, heightVh));
    },
    endTimelineSheetDrag() {
      this.isDraggingTimelineSheet = false;
      document.removeEventListener('mousemove', this.onTimelineSheetDrag);
      document.removeEventListener('mouseup', this.endTimelineSheetDrag);
      document.removeEventListener('touchmove', this.onTimelineSheetDrag);
      document.removeEventListener('touchend', this.endTimelineSheetDrag);
      this.persistTimelineSheetHeight();
    },
    setupDataPolling() {
      this.getDataAndStartPolling();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    },
    tearDownDataPolling() {
      this.stopPolling();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    },
    getDataAndStartPolling(skipLoading = false) {
      if (!this.requiresRefresh
      && (!this.gotDataAt
        || (Date.now() > this.gotDataAt + pollingNbIotDataInterval))) {
        this.getData(skipLoading);
      }
      this.startPolling();
    },
    getData(skipLoading = false) {
      this.$store.dispatch(
        'nbIot/getNbIotAsset',
        {
          token: this.t, assetId: this.assetId, bleId: this.bleId, skipLoading,
        },
      );
    },
    startPolling() {
      this.$store.dispatch(
        'nbIot/startPolling',
        { token: this.t, assetId: this.assetId, bleId: this.bleId },
      );
    },
    stopPolling() {
      // stop data polling
      this.$store.dispatch('nbIot/stopPolling');
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.getDataAndStartPolling(true);
      } else {
        this.stopPolling();
      }
    },
    getAddressDisplayName(address) {
      return constructDisplayAddress(address);
    },
    setTemperatureUnit(isCelsius) {
      this.$store.dispatch('user/setTemperatureUnits', isCelsius);
    },
    displayTemperatureChart() {
      this.temperatureChartIsVisible = true;
      this.tearDownDataPolling();
    },
    closeTemperatureChart() {
      this.temperatureChartIsVisible = false;
      this.setupDataPolling();
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/styles/_updated-variables.scss";

.tracking-info-card {
  margin: 0.75rem;
  margin-right: auto;
  width: fit-content;
  max-width: 66vw;
  border-radius: 0.5rem !important;

  @media (max-width: 959px) {
    margin: 0.5rem;
    width: auto;
    max-width: calc(100vw - 1rem);
  }

  &__content {
    padding: 0.75rem !important;

    @media (max-width: 959px) {
      padding: 0.375rem 0.5rem !important;
    }
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
  }

  &__reference {
    flex: 1;
    min-width: 0;
  }

  &__status {
    flex-shrink: 0;
  }

  &__chip-row {
    display: flex;
    align-items: center;
    gap: 0.375rem;

    .tracking-info-card__label {
      margin-bottom: 0;
    }
  }

  &__chip {
    flex-shrink: 0;
    align-self: center;
  }

  &__label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    color: $gray3;
    font-weight: bold;
    margin-bottom: 0.125rem;
    letter-spacing: 0.03125rem;

    @media (max-width: 959px) {
      font-size: 0.5625rem;
    }
  }

  &__value {
    font-size: 1rem;
    color: $gray1;
    word-wrap: break-word;

    @media (max-width: 959px) {
      font-size: 0.8125rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__subscript {
    margin-top: 0.125rem;
    font-size: 0.6875rem;
    color: $gray2;
    white-space: nowrap;

    @media (max-width: 959px) {
      font-size: 0.5625rem;
    }
  }

  &__actions {
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:hover {
      background-color: rgba(69, 190, 193, 0.08);
    }

    @media (max-width: 959px) {
      padding: 0.125rem 0.5rem;
    }
  }

  &__actions-text {
    font-size: 0.8125rem;
    color: $tealBright;
    font-weight: 500;
    padding-left: 0.25rem;

    @media (max-width: 959px) {
      font-size: 0.6875rem;
    }
  }

  &__chevron {
    transition: transform 0.3s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__details-content {
    padding: 0.5rem 0.75rem 0.75rem;

    @media (max-width: 959px) {
      padding: 0.375rem 0.5rem 0.5rem;
    }
  }

  &__detail-item {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__detail-value {
    font-size: 0.875rem;
    color: $gray1;
    word-wrap: break-word;

    @media (max-width: 959px) {
      font-size: 0.75rem;
    }

    &--highlight {
      color: $tealBright;
    }

    &--muted {
      color: $gray3;
      font-style: italic;
    }
  }

  &__temp-row {
    display: flex;
    align-items: center;
    gap: 0;
  }

  &__temp-number {
    font-size: 0.875rem;
    font-weight: 500;
    color: $gray1;
    font-variant-numeric: tabular-nums;

    @media (max-width: 959px) {
      font-size: 0.75rem;
    }
  }

  &__temp-unit {
    font-size: 0.875rem;
    color: $gray1;
    width: 1.25rem;
    flex-shrink: 0;
    text-align: center;

    @media (max-width: 959px) {
      font-size: 0.75rem;
      width: 1rem;
    }
  }

  &__unit-toggle {
    display: inline-flex;
    border: 1px solid $gray3;
    border-radius: 0.25rem;
    overflow: hidden;
    margin-left: 0.375rem;
  }

  &__unit-btn {
    all: unset;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.0625rem 0.375rem;
    cursor: pointer;
    color: $gray3;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(&--active) {
      background-color: rgba(69, 190, 193, 0.1);
      color: $gray2;
    }

    &--active {
      background-color: $tealBright;
      color: #fff;
    }

    @media (max-width: 959px) {
      font-size: 0.8125rem;
      padding: 0.1rem 0.4rem;
    }
  }

  &__temp-link {
    font-size: 0.75rem;
    color: $tealBright;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }

    @media (max-width: 959px) {
      font-size: 0.625rem;
    }
  }

  // Skeleton layout
  &__skeleton {
    .skeleton-row--horizontal {
      display: flex;
      gap: 0.75rem;
    }

    .skeleton-col {
      &:first-child {
        flex: 1;
      }
      &:last-child {
        flex-shrink: 0;
      }
    }

    .skeleton-chip-row {
      display: flex;
      align-items: center;
      gap: 0.375rem;
    }
  }

  // Skeleton bars — at card level so they apply in both content and actions
  .skeleton-bar {
    background-color: $gray3;
    border-radius: 0.25rem;
    animation: skeleton-pulse 1.5s ease-in-out infinite;

    &--label {
      width: 5rem;
      height: 0.5rem;
      margin-bottom: 0.25rem;
    }

    &--value {
      width: 8rem;
      height: 0.875rem;
    }

    &--chip {
      width: 3.75rem;
      height: 1.25rem;
      border-radius: 0.625rem;
    }

    &--subscript {
      width: 3.5rem;
      height: 0.5rem;
      margin-top: 0.25rem;
    }

    &--icon {
      width: 1rem;
      height: 1rem;
      border-radius: 50%;
    }

    &--toggle-text {
      width: 4.5rem;
      height: 0.75rem;
      margin-left: 0.25rem;
    }

    &--detail {
      width: 7rem;
      height: 0.875rem;
    }
  }
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

// ── Timeline FAB (button for expanding timeline )──
.timeline-fab {
  position: fixed !important;
  bottom: 1.25rem;
  left: 0.75rem;
  z-index: 7;
  background-color: rgba(0, 0, 0, 0.75) !important;
  backdrop-filter: blur(0.5rem);
  box-shadow:
    0 0.1875rem 0.375rem rgba(0, 0, 0, 0.3),
    0 0 0 2px $tealDark !important;
  transition: box-shadow 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.9) !important;
    box-shadow:
      0 0.25rem 0.75rem rgba(0, 0, 0, 0.4),
      0 0 0 2px $tealBright !important;
  }

  .v-icon {
    color: $tealBright !important;
    margin-right: 0.375rem;
  }

  &--with-label {
    padding: 0 1rem !important;
    text-transform: none;
    letter-spacing: 0.02em;
  }

  &__label {
    font-size: 0.8rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    padding-left: 0;
    padding-bottom: 2px;
  }

  @media (max-width: 959px) {
    left: 0.5rem;
    min-height: 40px !important;
    padding: 0.5rem 1rem !important;
  }
}

// ── Timeline bottom sheet (resizable, height persisted) ──
.timeline-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 6;
  display: flex;
  flex-direction: column;
  min-height: 15vh;
  max-height: 85vh;
  background-color: #fff;
  border-radius: 0.75rem 0.75rem 0 0;
  box-shadow: 0 -0.25rem 1.5rem rgba(0, 0, 0, 0.35);
  pointer-events: auto;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.75rem;
    background-color: rgba(0, 0, 0, 0.82);
    backdrop-filter: blur(0.75rem);
    flex-shrink: 0;
    border-bottom: 2px solid $tealDark;
    cursor: grab;
    user-select: none;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
  }

  &__header-drag {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    pointer-events: none; /* drag is on parent __header */
  }

  &__header-drag--active {
    cursor: grabbing;
  }

  &__grip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
  }

  &__grip-line {
    width: 16px;
    height: 2px;
    border-radius: 2px;
    margin-bottom: 4px;
    background-color: $tealBright; /* reelables turquoise */

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__header-right {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    letter-spacing: 0.03em;
  }

  &__content {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1;
    min-height: 0;

    &::-webkit-scrollbar {
      width: 0.375rem;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(69, 190, 193, 0.35);
      border-radius: 0.1875rem;

      &:hover {
        background-color: rgba(69, 190, 193, 0.55);
      }
    }

    scrollbar-width: thin;
    scrollbar-color: rgba(69, 190, 193, 0.35) transparent;
  }
}

// ── Sheet transition ──
.timeline-sheet-enter-active,
.timeline-sheet-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.timeline-sheet-enter,
.timeline-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
<style lang="scss">
@import "@/styles/_updated-variables.scss";

.nb-iot-container {
  .v-timeline {
    background-color: #fff;
    padding: 0.5rem 0.25rem 0.375rem;

    @media (min-width: 960px) {
      padding: 1.875rem 0.9375rem 1.25rem;
    }
  }
  .v-timeline-item {
    color: $gray1;
    position: relative;
    font-size: 0.75rem;
    padding-bottom: 0.375rem !important;

    @media (min-width: 960px) {
      font-size: 0.875rem;
      padding-bottom: 1.5rem !important;
    }

    strong {
      font-weight: normal !important;
    }

    // Compact the date/time opposite slot on mobile
    @media (max-width: 959px) {
      .v-timeline-item__opposite {
        padding: 0.125rem 0 !important;
      }
      .v-timeline-item__body {
        padding: 0.125rem 0 !important;
      }
      .row {
        margin: 0 !important;
      }
      .col, .col-5, .col-1 {
        padding: 0.0625rem 0.25rem !important;
      }
    }

    &:not(:last-of-type) {
      &::after {
        content: '';
        position: absolute;
        width: 0.125rem;
        height: 100%;
        background-color: $tealLight;
        bottom: -0.25rem;
        left: 50%;
        transform: translateX(-50%);

        @media (min-width: 960px) {
          width: 0.1875rem;
          bottom: -1.0625rem;
        }
      }
    }

    // Clip the line on the first item so it doesn't extend above the dot
    &:first-of-type {
      &::after {
        @media (max-width: 959px) {
          height: 50%;
          top: auto;
        }
      }
    }
  }

  .v-timeline-item__dot {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba($tealLight, 0.5) !important;

    @media (max-width: 959px) {
      height: 1.25rem !important;
      width: 1.25rem !important;
      min-width: 1.25rem !important;
    }
  }
  .v-timeline-item__inner-dot {
    background-color: $tealLight !important;
    height: 1rem;
    width: 1rem;

    @media (max-width: 959px) {
      height: 0.625rem !important;
      width: 0.625rem !important;
    }

    .v-icon {
      display: none;
    }
  }
  .v-timeline-item:first-of-type {
    color: $tealBright;
    .v-timeline-item__inner-dot {
      background-color: $tealDark !important;
    }
  }
  .v-timeline-item:last-of-type {
    .v-timeline-item__inner-dot {
      background-color: $gray2 !important;
    }
    .v-timeline-item__dot {
      background-color: rgba($gray2, 0.5) !important;
    }
  }

  // Compact event details on mobile
  @media (max-width: 959px) {
    .timeline-item-content .event-details {
      .event-type-icon {
        margin-right: 0.375rem;
        font-size: 1rem !important;
      }
      .event-detail {
        strong {
          font-size: 0.75rem;
          line-height: 1.3;
        }
        .text-caption {
          font-size: 0.625rem !important;
          line-height: 1.2;
        }
      }
    }
  }
}
</style>
